import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { ENDPOINT } from 'constants';
import { buildSampleVariables } from 'utils/sampleVariableValues';

// Read-only "how does this look" preview for a saved template, opened from the list's
// eye icon. Reuses the same render pipeline (and same sample-data approach) as the
// live preview inside EmailTemplateModal, just without any of the editing UI.
const EmailTemplatePreviewModal = ({ open, onClose, template }) => {
  const { user } = useSelector((state) => state.auth);
  const [contentTab, setContentTab] = useState(0);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sampleVariables = useMemo(
    () => buildSampleVariables(template?.availableVariables),
    [template?.availableVariables],
  );

  useEffect(() => {
    if (!open || !template) return;

    setContentTab(0);

    if (!template.body) {
      setHtml('');
      return;
    }

    setLoading(true);
    setError('');
    axios
      .post(
        `${ENDPOINT}api/v1/admin/reminder-templates/preview`,
        {
          subject: template.subject,
          body: template.body,
          bodyFormat: template.bodyFormat || 'text',
          variables: sampleVariables,
        },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } },
      )
      .then(({ data }) => setHtml(data.data.html))
      .catch((err) => {
        console.error('Failed to render email preview:', err);
        setError('Could not render this template — check its body/subject for errors.');
      })
      .finally(() => setLoading(false));
  }, [open, template, sampleVariables, user?.accessToken]);

  const substituteVariables = (text) => {
    let result = text || '';
    Object.entries(sampleVariables).forEach(([key, value]) => {
      result = result.split(`{${key}}`).join(value);
    });
    return result;
  };

  const hasEmail = !!(template?.subject || template?.body);
  const hasSms = !!template?.smsBody;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preview — {template?.name}</DialogTitle>
      <DialogContent dividers>
        {hasEmail && hasSms && (
          <Tabs value={contentTab} onChange={(_, v) => setContentTab(v)} sx={{ mb: 2, minHeight: 36 }}>
            <Tab label="Email" sx={{ minHeight: 36, py: 1 }} />
            <Tab label="SMS" sx={{ minHeight: 36, py: 1 }} />
          </Tabs>
        )}

        {(!hasSms || contentTab === 0) && hasEmail && (
          <MDBox>
            <MDTypography variant="caption" fontWeight="medium" display="block" mb={1}>
              Subject: {substituteVariables(template?.subject) || '(no subject)'}
            </MDTypography>
            {loading ? (
              <MDBox
                sx={{ width: '100%', height: 480, border: '1px dashed #ccc', borderRadius: 2 }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.secondary"
              >
                Rendering…
              </MDBox>
            ) : error ? (
              <MDBox
                sx={{ width: '100%', height: 480, border: '1px dashed #ccc', borderRadius: 2 }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="error.main"
                textAlign="center"
                px={2}
              >
                {error}
              </MDBox>
            ) : html ? (
              <iframe
                title="Email preview"
                srcDoc={html}
                style={{ width: '100%', height: 480, border: '1px solid #e0e0e0', borderRadius: 8 }}
              />
            ) : (
              <MDBox
                sx={{ width: '100%', height: 480, border: '1px dashed #ccc', borderRadius: 2 }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.secondary"
              >
                This template has no email body.
              </MDBox>
            )}
          </MDBox>
        )}

        {(!hasEmail || contentTab === 1) && hasSms && (
          <MDBox>
            <MDTypography variant="caption" fontWeight="medium" display="block" mb={1}>
              SMS message — with sample data substituted
            </MDTypography>
            <MDBox
              sx={{
                maxWidth: 260,
                minHeight: 70,
                bgcolor: 'grey.100',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: '16px 16px 16px 4px',
                p: 2,
                fontSize: 13,
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
              }}
            >
              {substituteVariables(template?.smsBody)}
            </MDBox>
          </MDBox>
        )}

        {!hasEmail && !hasSms && (
          <MDTypography variant="body2" color="text.secondary">
            This template has no email or SMS content yet.
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplatePreviewModal;
